import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useData } from '../../contexts/DataContext';
import { ArticleAutocomplete } from '../../components/ui/article-autocomplete';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Zap, Save, MapPin, Truck, Star, TrendingDown, Package, Camera, X, Plus, Trash2, Edit } from 'lucide-react';
import { useQrScanner } from '../../hooks/useQrScanner';
import { Card, CardContent } from '../../components/ui/card';

const QuickReceiptDialog = ({ open, onClose, onSuccess, editReceipt }) => {
  const { plants, selectedPlant } = useData();
  const [loading, setLoading] = useState(false);
  const [articles, setArticles] = useState([]);
  const [bins, setBins] = useState([]);
  const [scanning, setScanning] = useState(false);
  const { start: startQr, stop: stopQr } = useQrScanner();
  const scanContainerId = useRef('quick-receipt-qr-' + Date.now()).current;
  
  // Multiple items list
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [plantId, setPlantId] = useState(selectedPlant || '');
  
  // Current item being added/edited
  const [currentItem, setCurrentItem] = useState({
    article: null,
    quantity: '',
    bin_id: '',
    lot_number: '',
    supplier_id: '',
    supplier_name: ''
  });
  const [editingIndex, setEditingIndex] = useState(null);

  useEffect(() => {
    if (open) {
      fetchArticles();
      fetchBins();
      if (editReceipt && editReceipt.receipt_type === 'quick') {
        // Load existing receipt for editing
        setNotes(editReceipt.notes || '');
        setPlantId(editReceipt.plant_id || selectedPlant || '');
        if (editReceipt.items?.length > 0) {
          const loadedItems = editReceipt.items.map(item => ({
            article: { id: item.article_id, name: item.article_name, code: item.article_code, unit: item.unit },
            quantity: item.quantity || 1,
            bin_id: item.bin_allocations?.[0]?.bin_id || '',
            lot_number: item.lot_number || '',
            supplier_id: editReceipt.supplier_id || '',
            supplier_name: editReceipt.supplier_name || ''
          }));
          setItems(loadedItems);
        }
      }
    } else {
      stopQr();
      setScanning(false);
    }
  }, [open, editReceipt]);

  const fetchArticles = async () => {
    try {
      const response = await api.get('/articles');
      setArticles(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const fetchBins = async () => {
    try {
      const response = await api.get('/bins');
      setBins(response.data);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // QR Scanner - for adding articles by scanning
  const handleStartScan = useCallback(async () => {
    setScanning(true);
    try {
      await startQr(scanContainerId, (text) => {
        handleQrResult(text);
      });
    } catch (e) {
      toast.error('Nu s-a putut porni camera');
      setScanning(false);
    }
  }, [scanContainerId, startQr]);

  const handleStopScan = useCallback(async () => {
    await stopQr();
    setScanning(false);
  }, [stopQr]);

  const handleQrResult = useCallback(async (scannedText) => {
    await stopQr();
    setScanning(false);

    // Parse QR code - support formats: ART:code, just code, or article name
    let articleCode = scannedText;
    if (scannedText.startsWith('ART:')) {
      articleCode = scannedText.substring(4);
    }

    // Try to find the article
    const matched = articles.find(
      a => a.code === articleCode || a.id === articleCode ||
           (a.code && scannedText.includes(a.code)) ||
           (a.name && scannedText.toUpperCase() === a.name.toUpperCase())
    );

    if (matched) {
      // Auto-fill article with supplier + BIN
      handleArticleSelect(matched);
      toast.success(`Articol gasit: ${matched.name}`, { duration: 3000 });
    } else {
      toast.error(`Articol negasit pentru codul: ${articleCode}`);
    }
  }, [articles, stopQr]);

  const articleSuppliers = useMemo(() => {
    if (!currentItem.article) return [];
    const full = articles.find(a => a.id === currentItem.article.id);
    return (full?.suppliers || []).filter(s => s.supplier_id && s.supplier_name);
  }, [currentItem.article, articles]);

  const handleArticleSelect = (article) => {
    const autoFilledBin = article?.default_bin_id || '';
    const full = articles.find(a => a.id === article?.id);
    const suppliers = (full?.suppliers || []).filter(s => s.supplier_id && s.supplier_name);

    let autoSupplierId = '';
    let autoSupplierName = '';

    if (suppliers.length === 1) {
      autoSupplierId = suppliers[0].supplier_id;
      autoSupplierName = suppliers[0].supplier_name;
    } else if (suppliers.length > 1) {
      const preferred = suppliers.find(s => s.is_preferred);
      if (preferred) {
        autoSupplierId = preferred.supplier_id;
        autoSupplierName = preferred.supplier_name;
      }
    }

    setCurrentItem(prev => ({
      ...prev,
      article,
      bin_id: autoFilledBin || prev.bin_id,
      supplier_id: autoSupplierId,
      supplier_name: autoSupplierName
    }));

    if (autoFilledBin) {
      const binInfo = bins.find(b => b.id === autoFilledBin);
      if (binInfo) toast.info(`BIN preferat: ${binInfo.cabinet} > ${binInfo.shelf} > ${binInfo.position}`, { duration: 3000 });
    }
    if (autoSupplierId && suppliers.length === 1) {
      toast.info(`Furnizor unic: ${autoSupplierName}`, { duration: 3000 });
    } else if (autoSupplierId && suppliers.length > 1) {
      toast.info(`Furnizor preferat: ${autoSupplierName}`, { duration: 3000 });
    }
  };

  const handleSupplierSelect = (supplierId) => {
    if (supplierId === '__none__') {
      setCurrentItem(prev => ({ ...prev, supplier_id: '', supplier_name: '' }));
      return;
    }
    const sup = articleSuppliers.find(s => s.supplier_id === supplierId);
    setCurrentItem(prev => ({
      ...prev,
      supplier_id: supplierId,
      supplier_name: sup?.supplier_name || ''
    }));
  };

  // Add item to list
  const handleAddItem = () => {
    if (!currentItem.article) return toast.error('Selecteaza un articol');
    if (!currentItem.quantity || currentItem.quantity <= 0) return toast.error('Introduceti cantitatea');
    if (!currentItem.bin_id) return toast.error('Selecteaza un BIN');

    if (editingIndex !== null) {
      // Update existing item
      const newItems = [...items];
      newItems[editingIndex] = { ...currentItem };
      setItems(newItems);
      setEditingIndex(null);
    } else {
      // Add new item
      setItems(prev => [...prev, { ...currentItem }]);
    }
    
    // Reset form for next item
    resetCurrentItem();
    toast.success('Articol adaugat in lista');
  };

  const handleEditItem = (index) => {
    setCurrentItem({ ...items[index] });
    setEditingIndex(index);
  };

  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
    if (editingIndex === index) {
      resetCurrentItem();
      setEditingIndex(null);
    }
  };

  const resetCurrentItem = () => {
    setCurrentItem({
      article: null,
      quantity: '',
      bin_id: '',
      lot_number: '',
      supplier_id: '',
      supplier_name: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // If there's a current item being edited, validate we have items in list
    if (items.length === 0) {
      // Try to add current item first if it's valid
      if (currentItem.article && currentItem.quantity > 0 && currentItem.bin_id) {
        const itemsToSave = [{ ...currentItem }];
        await saveReceipts(itemsToSave);
        return;
      }
      return toast.error('Adaugati cel putin un articol');
    }

    await saveReceipts(items);
  };

  const saveReceipts = async (itemsToSave) => {
    try {
      setLoading(true);
      
      if (editReceipt) {
        // Update existing receipt (single item)
        const item = itemsToSave[0];
        await api.put(`/wms/receipts/${editReceipt.id}`, {
          notes: notes,
          supplier_id: item.supplier_id || null,
          supplier_name: item.supplier_name || null,
          items: [{
            article_id: item.article.id,
            article_name: item.article.name,
            article_code: item.article.code,
            quantity: item.quantity,
            unit: item.article.unit || 'BUC',
            unit_price: 0, discount_percent: 0, total_price: 0,
            lot_number: item.lot_number,
            bin_allocations: [{ bin_id: item.bin_id, quantity: item.quantity }]
          }]
        });
        toast.success('Receptie rapida actualizata!');
      } else {
        // Create new receipts - one per item (or batch endpoint if available)
        for (const item of itemsToSave) {
          const payload = {
            article_id: item.article.id,
            quantity: item.quantity,
            bin_id: item.bin_id,
            lot_number: item.lot_number,
            notes: notes,
            plant_id: plantId,
            supplier_id: item.supplier_id || null,
            supplier_name: item.supplier_name || null
          };
          await api.post('/wms/quick-receipt', payload);
        }
        toast.success(`${itemsToSave.length} ${itemsToSave.length === 1 ? 'receptie rapida inregistrata' : 'receptii rapide inregistrate'}!`);
      }
      
      onSuccess?.();
      onClose?.();
      resetForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eroare la salvare');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setItems([]);
    setNotes('');
    setPlantId(selectedPlant || '');
    resetCurrentItem();
    setEditingIndex(null);
  };

  const cheapestId = useMemo(() => {
    if (articleSuppliers.length < 2) return null;
    const withPrice = articleSuppliers.filter(s => s.price > 0);
    if (withPrice.length === 0) return null;
    return withPrice.reduce((min, s) => s.price < min.price ? s : min, withPrice[0]).supplier_id;
  }, [articleSuppliers]);

  const totalQuantity = items.reduce((sum, item) => sum + (item.quantity || 0), 0);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-xl flex items-center gap-2" data-testid="quick-receipt-title">
            <Zap className="w-5 h-5 text-orange-600" />
            {editReceipt ? 'Editare Receptie Rapida' : 'Receptie Rapida'}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Items list */}
          {items.length > 0 && (
            <div className="space-y-2" data-testid="items-list">
              <Label className="text-base font-semibold">Articole adaugate ({items.length})</Label>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {items.map((item, idx) => {
                  const binInfo = bins.find(b => b.id === item.bin_id);
                  return (
                    <Card key={idx} className={`border ${editingIndex === idx ? 'border-blue-400 bg-blue-50' : ''}`}>
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{item.article?.name}</p>
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <Badge variant="outline" className="text-[10px]">{item.article?.code}</Badge>
                              <span>{item.quantity} {item.article?.unit || 'buc'}</span>
                              {binInfo && <span className="text-slate-400">• {binInfo.cabinet}/{binInfo.shelf}/{binInfo.position}</span>}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleEditItem(idx)} className="h-7 w-7 p-0">
                              <Edit className="w-3.5 h-3.5" />
                            </Button>
                            <Button type="button" variant="ghost" size="sm" onClick={() => handleRemoveItem(idx)} className="h-7 w-7 p-0 text-red-600">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              <div className="bg-slate-50 p-2 rounded text-sm text-center">
                Total: <span className="font-bold">{items.length}</span> articole, <span className="font-bold">{totalQuantity}</span> buc
              </div>
            </div>
          )}

          {/* Separator */}
          {items.length > 0 && (
            <div className="border-t pt-4">
              <Label className="text-sm text-slate-500">
                {editingIndex !== null ? 'Editeaza articol' : 'Adauga alt articol'}
              </Label>
            </div>
          )}

          {/* QR Scanner */}
          {scanning && (
            <div className="space-y-2" data-testid="qr-scanner-section">
              <div className="relative rounded-xl overflow-hidden bg-black">
                <div id={scanContainerId} className="w-full" style={{ minHeight: 250 }} />
                <button
                  type="button"
                  onClick={handleStopScan}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1.5 hover:bg-black/80 z-10"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <p className="text-xs text-center text-slate-500">Scanati codul QR al articolului</p>
            </div>
          )}

          {/* Article */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Articol *</Label>
              {!scanning && !currentItem.article && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={handleStartScan}
                  className="h-7 text-xs gap-1.5 border-blue-200 text-blue-700 hover:bg-blue-50"
                  data-testid="scan-qr-btn"
                >
                  <Camera className="w-3.5 h-3.5" />
                  Scan QR
                </Button>
              )}
            </div>
            <ArticleAutocomplete
              articles={articles}
              onSelect={handleArticleSelect}
              placeholder="Cauta articol sau scaneaza QR..."
            />
            {currentItem.article && (
              <div className="bg-blue-50 p-2.5 rounded-lg border border-blue-200 flex items-center justify-between">
                <div>
                  <p className="font-semibold text-sm text-blue-900">{currentItem.article.name}</p>
                  <p className="text-xs text-blue-600 font-mono">{currentItem.article.code}</p>
                </div>
                {currentItem.supplier_id && currentItem.supplier_name && (
                  <Badge variant="outline" className="text-[10px] border-green-300 text-green-700 bg-green-50 gap-1">
                    <Truck className="w-2.5 h-2.5" />{currentItem.supplier_name}
                  </Badge>
                )}
              </div>
            )}
          </div>

          {/* Supplier Selection - only when article has suppliers */}
          {currentItem.article && articleSuppliers.length > 0 && (
            <div className="space-y-2" data-testid="supplier-section">
              <Label className="flex items-center gap-1.5">
                <Truck className="w-3.5 h-3.5 text-slate-500" />
                Furnizor
              </Label>
              <div className="grid gap-2">
                {articleSuppliers.map(sup => {
                  const isSelected = currentItem.supplier_id === sup.supplier_id;
                  return (
                    <button
                      key={sup.supplier_id}
                      type="button"
                      onClick={() => handleSupplierSelect(sup.supplier_id)}
                      data-testid={`supplier-card-${sup.supplier_id}`}
                      className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                        isSelected
                          ? 'border-blue-500 bg-blue-50 shadow-sm'
                          : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                            isSelected ? 'border-blue-500 bg-blue-500' : 'border-slate-300'
                          }`}>
                            {isSelected && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </div>
                          <span className={`text-sm font-medium ${isSelected ? 'text-blue-900' : 'text-slate-700'}`}>
                            {sup.supplier_name}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          {sup.is_preferred && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-blue-300 text-blue-700 bg-blue-50">
                              <Star className="w-2.5 h-2.5 mr-0.5" />PREFERAT
                            </Badge>
                          )}
                          {cheapestId === sup.supplier_id && (
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0 border-green-300 text-green-700 bg-green-50">
                              <TrendingDown className="w-2.5 h-2.5 mr-0.5" />IEFTIN
                            </Badge>
                          )}
                          {sup.price > 0 && (
                            <span className="text-xs text-slate-500">{sup.price.toFixed(2)} RON</span>
                          )}
                        </div>
                      </div>
                      {sup.supplier_article_name && (
                        <p className="text-[11px] text-slate-400 mt-1 ml-5">
                          Cod furnizor: {sup.supplier_article_name}
                        </p>
                      )}
                    </button>
                  );
                })}
                {/* No supplier option */}
                <button
                  type="button"
                  onClick={() => handleSupplierSelect('__none__')}
                  data-testid="supplier-none"
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all ${
                    !currentItem.supplier_id
                      ? 'border-amber-400 bg-amber-50'
                      : 'border-dashed border-slate-200 bg-white hover:border-slate-300'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full border-2 flex items-center justify-center ${
                      !currentItem.supplier_id ? 'border-amber-500 bg-amber-500' : 'border-slate-300'
                    }`}>
                      {!currentItem.supplier_id && <div className="w-1.5 h-1.5 rounded-full bg-white" />}
                    </div>
                    <Package className="w-3.5 h-3.5 text-slate-400" />
                    <span className="text-sm text-slate-500">Fara furnizor / Reorganizare stoc</span>
                  </div>
                </button>
              </div>
            </div>
          )}

          {/* Quantity + LOT */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Cantitate *</Label>
              <Input
                type="number"
                value={currentItem.quantity}
                onChange={(e) => setCurrentItem({...currentItem, quantity: e.target.value === '' ? '' : parseInt(e.target.value) || 0})}
                min="1" step="1"
                placeholder="Introduceti cantitatea"
                className="h-12 text-lg"
                data-testid="quantity-input"
              />
            </div>
            <div className="space-y-2">
              <Label>LOT/Batch</Label>
              <Input
                value={currentItem.lot_number}
                onChange={(e) => setCurrentItem({...currentItem, lot_number: e.target.value})}
                placeholder="ex: LOT-2025-001"
                className="h-12"
                data-testid="lot-input"
              />
            </div>
          </div>

          {/* BIN */}
          <div className="space-y-2">
            <Label>Locatie BIN *</Label>
            <Select value={currentItem.bin_id} onValueChange={(v) => setCurrentItem({...currentItem, bin_id: v})}>
              <SelectTrigger className="h-12" data-testid="bin-select">
                <SelectValue placeholder="Selecteaza BIN" />
              </SelectTrigger>
              <SelectContent>
                {bins.map(b => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.cabinet} &gt; {b.shelf} &gt; {b.position} ({b.code})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {currentItem.article?.default_bin_id && currentItem.bin_id === currentItem.article.default_bin_id && (
              <div className="flex items-center gap-1.5 text-xs text-green-600 bg-green-50 p-2 rounded-lg border border-green-200">
                <MapPin className="w-3.5 h-3.5" />
                <span>BIN preferat al articolului - auto-completat</span>
              </div>
            )}
          </div>

          {/* Add to list button */}
          {!editReceipt && (
            <Button
              type="button"
              variant="outline"
              onClick={handleAddItem}
              className="w-full h-10 border-dashed border-2"
              data-testid="add-item-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              {editingIndex !== null ? 'Actualizeaza articol' : 'Adauga articol in lista'}
            </Button>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Observatii..."
              rows={2}
              data-testid="notes-input"
            />
          </div>

          <DialogFooter className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => { onClose(); resetForm(); }}>
              Anuleaza
            </Button>
            <Button type="submit" disabled={loading} className="bg-green-600 hover:bg-green-700 h-12" data-testid="save-receipt-btn">
              <Save className="w-5 h-5 mr-2" />
              {loading ? 'Salvare...' : editReceipt ? 'Actualizeaza' : items.length > 0 ? `Salveaza ${items.length} receptii` : 'Salveaza Receptia'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default QuickReceiptDialog;
